import { useState, useEffect } from 'react'
import { format } from 'date-fns'
import {
  Package,
  Plus,
  Eye,
  Edit,
  Trash2,
  RefreshCw,
  Download,
  Search,
  Filter,
  Calendar,
  Building,
  ArrowRightLeft,
  User,
  Truck,
  FileText,
  DollarSign,
  CheckCircle,
  AlertCircle,
  X,
  TrendingUp
} from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card'
import InlineLoader from '../../../components/ui/InlineLoader'
import ButtonSpinner from '../../../components/ui/ButtonSpinner'
import warehouseTransferService from '../../../services/warehouseTransferService'
import purchaseOrderService from '../../../services/purchaseOrderService'
import branchService from '../../../services/branchService'
import RawMaterialsService from '../../../services/rawMaterialsService'
import rawMaterialInventoryService from '../../../services/rawMaterialInventoryService'
import productInventoryService from '../../../services/productInventoryService'
import AuthService from '../../../services/authService'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 animate-in slide-in-from-right-full duration-300 ${
    type === 'success' 
      ? 'bg-green-500 text-white' 
      : type === 'error'
      ? 'bg-red-500 text-white'
      : type === 'warning'
      ? 'bg-yellow-500 text-white'
      : 'bg-blue-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : 
     type === 'error' ? <AlertCircle className="h-5 w-5" /> : 
     <AlertCircle className="h-5 w-5" />}
    <span className="font-medium">{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2 text-white/80 hover:text-white">
      <X className="h-4 w-4" />
    </button>
  </div>
)
import { isManagerRole } from '../../../config/roles'

// Helper function to safely convert numbers
const safeNumber = (value, defaultValue = 0) => {
  const num = parseFloat(value)
  return isNaN(num) ? defaultValue : num
}

// Helper function to clean transferredBy display (backend bug fix)
const cleanTransferredBy = (transferredBy) => {
  if (!transferredBy) return 'N/A'
  if (typeof transferredBy === 'string' && transferredBy.includes('GMT')) {
    return 'N/A' // Backend sent date instead of name
  }
  return transferredBy
}

const ProductsTransfer = () => {
  const user = AuthService.getCurrentUser()
  const isManager = isManagerRole(user?.role)
  const userBranchId = user?.selectedBranch?._id || user?.branchId || user?.branch?._id

  // State
  const [transferNotes, setTransferNotes] = useState([])
  const [filteredTransferNotes, setFilteredTransferNotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [branchFilter, setBranchFilter] = useState(isManager ? (userBranchId || '') : '')
  const [toast, setToast] = useState(null)

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedTransfer, setSelectedTransfer] = useState(null)

  // Form data for create/edit
  const [formData, setFormData] = useState({
    purchaseOrder: '',
    warehouse: '',
    transferredBy: '',
    transferredDate: format(new Date(), 'yyyy-MM-dd'),
    transferredItems: [],
    note: ''
  })

  // Additional data
  const [purchaseOrders, setPurchaseOrders] = useState([])
  const [warehouses, setWarehouses] = useState([])
  const [branches, setBranches] = useState([])
  const [formLoading, setFormLoading] = useState(false)

  // Toast helper
  const showToast = (message, type = 'info') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 5000)
  }

  // Fetch transfer notes
  const fetchTransferNotes = async () => {
    try {
      console.log('🔄 Starting to fetch transfer notes...')
      setLoading(true)
      const response = await warehouseTransferService.getAllTransferNotes()
      console.log('📄 Transfer notes response:', response)
      
      if (response.success || response.wtns) {
        const notes = response.wtns || []
        console.log('✅ Transfer notes fetched successfully:', notes.length, 'notes')
        
        // Debug: Check transferredBy values in refreshed data
        notes.forEach((note, index) => {
          console.log(`🔍 Note ${index} transferredBy:`, note.transferredBy, typeof note.transferredBy)
        })
        
        setTransferNotes(notes)
        setFilteredTransferNotes(notes)
      } else {
        console.warn('❌ Failed to fetch transfer notes:', response)
        showToast('Failed to fetch transfer notes', 'error')
      }
    } catch (error) {
      console.error('❌ Error fetching transfer notes:', error)
      showToast('Error fetching transfer notes', 'error')
    } finally {
      setLoading(false)
      console.log('🏁 Finished fetching transfer notes')
    }
  }

  // Fetch additional data (POs, warehouses, branches)
  const fetchAdditionalData = async () => {
    try {
      console.log('🔄 Starting to fetch additional data (POs, branches)...')
      
      // Fetch data individually to handle errors gracefully
      const fetchPromises = [
        purchaseOrderService.getAllPurchaseOrders().catch(err => {
          console.warn('❌ Purchase orders not available:', err.message)
          return { success: false, data: [] }
        }),
        branchService.getAllBranches().catch(err => {
          console.warn('❌ Branches not available:', err.message)
          return { success: false, data: [] }
        })
      ]

      console.log('⏳ Waiting for API responses...')
      const [posResponse, branchesResponse] = await Promise.all(fetchPromises)
      console.log('📥 API responses received:', { posResponse, branchesResponse })

      if (posResponse.success) {
        const allOrders = posResponse.data?.purchaseOrders || []
        console.log('📦 Fetched All Purchase Orders:', allOrders)
        
        // Filter POs: only show approved POs where outlet ordered from warehouse
        const filteredOrders = allOrders.filter(po => {
          const isApproved = po.status === 'approved'
          const isWarehouseSupplier = po.supplierType === 'warehouse'
          const isRequestingBranchOutlet = po.requestingBranch?.type === 'outlet' || po.requestingBranch?.type === 'branch'
          
          console.log(`📋 PO ${po.poNumber}: status="${po.status}" supplierType="${po.supplierType}" requestingBranchType="${po.requestingBranch?.type}" -> included: ${isApproved && isWarehouseSupplier && isRequestingBranchOutlet}`)
          
          return isApproved && isWarehouseSupplier && isRequestingBranchOutlet
        })
        
        console.log('📦 Filtered POs (approved, warehouse supplier, outlet requesting):', filteredOrders)
        console.log('📦 First filtered PO structure (if exists):', filteredOrders[0] ? JSON.stringify(filteredOrders[0], null, 2) : 'No filtered POs available')
        setPurchaseOrders(filteredOrders)
        
        if (filteredOrders.length === 0) {
          console.log('ℹ️ No approved warehouse-supplied purchase orders found for outlet branches.')
        }
      } else {
        setPurchaseOrders([])
        console.warn('Failed to fetch purchase orders')
      }

      // Get warehouses from branches endpoint (type == "warehouse")
      if (branchesResponse.success) {
        const allBranches = branchesResponse.data?.branches || branchesResponse.data || []
        console.log('🏢 All branches fetched:', allBranches)
        
        // Filter branches where type == "warehouse"
        const warehouseBranches = allBranches.filter(branch => branch.type === 'warehouse')
        console.log('🏪 Filtered warehouse branches:', warehouseBranches)
        
        setBranches(allBranches)
        setWarehouses(warehouseBranches)
        
        if (warehouseBranches.length === 0) {
          console.log('⚠️ No warehouse type branches found. Check if branches have type="warehouse"')
        }
      } else {
        setBranches([])
        setWarehouses([])
        console.warn('Failed to fetch branches for warehouses')
      }
    } catch (error) {
      console.error('Error fetching additional data:', error)
      // Set default values to allow the page to function
      setPurchaseOrders([])
      setWarehouses([
        { _id: '1', name: 'Main Warehouse', code: 'WH001', location: 'Central' },
        { _id: '2', name: 'Secondary Warehouse', code: 'WH002', location: 'East' }
      ])
      setBranches([])
    }
  }

  // Initialize data
  useEffect(() => {
    console.log('🚀 ProductsTransfer component mounted, initializing data...')
    const initializeData = async () => {
      try {
        console.log('🔄 Starting parallel data fetch...')
        await Promise.all([fetchTransferNotes(), fetchAdditionalData()])
        console.log('✅ All data initialization completed')
      } catch (error) {
        console.error('❌ Error during data initialization:', error)
      }
    }
    initializeData()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Filter transfer notes
  useEffect(() => {
    let filtered = [...transferNotes]

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(transfer =>
        transfer.wtnNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transfer.purchaseOrder?.poNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transfer.transferredBy?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Branch filter (for managers)
    if (branchFilter) {
      filtered = filtered.filter(transfer =>
        transfer.requestingBranch === branchFilter ||
        transfer.requestingBranch?._id === branchFilter
      )
    }

    setFilteredTransferNotes(filtered)
  }, [transferNotes, searchTerm, branchFilter])

  // Handle create transfer
  const handleCreateTransfer = async () => {
    try {
      setFormLoading(true)
      
      console.log('📋 Current form data before validation:', formData)
      
      // Validate required fields
      if (!formData.purchaseOrder) {
        showToast('Please select a purchase order', 'error')
        return
      }
      
      if (!formData.transferredBy) {
        showToast('Please enter who transferred the items', 'error')
        return
      }
      
      if (!formData.transferredItems || formData.transferredItems.length === 0) {
        showToast('Please select a purchase order with items to transfer', 'error')
        return
      }
      
      // Validate transferred items have required fields
      const itemsMissingPoItemId = formData.transferredItems.filter(item => !item.poItemId)
      if (itemsMissingPoItemId.length > 0) {
        console.error('❌ Items missing poItemId:', itemsMissingPoItemId)
        showToast('Some transfer items are missing PO item ID. Please reselect the purchase order.', 'error')
        return
      }

      // Pre-submission validation: check for items exceeding ordered quantity or available stock
      const overOrdered = []
      const overStock = []

      for (const item of formData.transferredItems) {
        const qty = safeNumber(item.transferredQuantity, 0)
        const ord = safeNumber(item.orderedQuantity, 0)
        const avail = item.availableStock !== null && item.availableStock !== undefined ? safeNumber(item.availableStock, null) : null

        if (ord > 0 && qty > ord) {
          overOrdered.push(`${item.itemName} (${qty} > ordered ${ord})`)
        }
        if (avail !== null && qty > avail) {
          overStock.push(`${item.itemName} (${qty} > warehouse ${avail})`)
        }
      }

      if (overOrdered.length > 0) {
        showToast(`Cannot transfer: Quantity exceeds PO ordered amount for ${overOrdered.slice(0, 2).join(', ')}${overOrdered.length > 2 ? ` and ${overOrdered.length - 2} more` : ''}`, 'error')
        return
      }

      if (overStock.length > 0) {
        showToast(`Cannot transfer: Insufficient warehouse stock for ${overStock.slice(0, 2).join(', ')}${overStock.length > 2 ? ` and ${overStock.length - 2} more` : ''}`, 'error')
        return
      }

      // Filter out frontend-only fields (itemName, itemUnit) before sending to backend
      const transferredItemsForBackend = formData.transferredItems.map(item => ({
        poItemId: item.poItemId,
        transferredQuantity: safeNumber(item.transferredQuantity, 0),
        unitPrice: safeNumber(item.unitPrice, 0)
      }))
      
      const transferData = {
        purchaseOrder: formData.purchaseOrder,
        warehouse: String(formData.warehouse), // Ensure warehouse is a string
        transferredBy: formData.transferredBy,
        transferredDate: formData.transferredDate,
        transferredItems: transferredItemsForBackend,
        note: formData.note
      }

      console.log('📦 Transfer data being sent:', JSON.stringify(transferData, null, 2))
      console.log('🔍 Individual field validation:')
      console.log('  - purchaseOrder:', formData.purchaseOrder, typeof formData.purchaseOrder)
      console.log('  - warehouse:', formData.warehouse, typeof formData.warehouse)
      console.log('  - transferredItems:', formData.transferredItems)
      
      // Additional validation - check if IDs are valid ObjectId format (24 character hex string)
      const isValidObjectId = (id) => /^[0-9a-fA-F]{24}$/.test(id)
      
      console.log('🔍 Validating ID formats...')
      console.log('  - purchaseOrder format valid:', isValidObjectId(formData.purchaseOrder))
      
      if (!isValidObjectId(formData.purchaseOrder)) {
        console.error('❌ Invalid Purchase Order ID format:', formData.purchaseOrder)
        showToast('Invalid Purchase Order ID format', 'error')
        return
      }
      
      // Check if poItemIds are valid
      let hasInvalidPoItem = false
      formData.transferredItems.forEach((item, index) => {
        console.log(`  - poItem ${index} format valid:`, isValidObjectId(item.poItemId), item.poItemId)
        if (!isValidObjectId(item.poItemId)) {
          console.error(`❌ Invalid PO Item ID format at index ${index}:`, item.poItemId)
          showToast(`Invalid PO Item ID format in item ${index + 1}`, 'error')
          hasInvalidPoItem = true
        }
      })
      
      if (hasInvalidPoItem) {
        return
      }
      
      console.log('✅ All ID formats are valid, proceeding with API call...')
      
      // Check if transferredItems have proper structure
      if (formData.transferredItems && formData.transferredItems.length > 0) {
        console.log('🧪 First transferred item structure:')
        console.log('    ', JSON.stringify(formData.transferredItems[0], null, 2))
        
        // Validate each item has required fields for API
        formData.transferredItems.forEach((item, index) => {
          console.log(`🔍 Item ${index} validation:`)
          console.log(`   - poItemId: ${item.poItemId} (${typeof item.poItemId})`)
          console.log(`   - transferredQuantity: ${item.transferredQuantity} (${typeof item.transferredQuantity})`)
          console.log(`   - unitPrice: ${item.unitPrice} (${typeof item.unitPrice})`)
          
          if (!item.poItemId) {
            console.error(`❌ Item ${index} missing poItemId!`)
          }
        })
      }

      console.log('🔥 About to make API call...')
      const response = await warehouseTransferService.createTransferNote(transferData)
      console.log('🔥 API Response received:', response)
      console.log('🔥 Response.success:', response.success)
      console.log('🔥 Response.statusCode:', response.statusCode)
      console.log('🔥 Response.warehouseTransferNote:', response.warehouseTransferNote)
      
      // Check for success in multiple ways since API response format might vary
      const isSuccess = response?.success === true || 
                       response?.warehouseTransferNote ||
                       response?.data?.warehouseTransferNote ||
                       response?._id ||
                       response?.wtnNumber ||
                       response?.statusCode === 201 ||
                       response?.statusCode === 200 ||
                       (response && !response.error && response.success !== false)
      
      console.log('🔥 Success check result:', isSuccess)
      
      if (isSuccess) {
        console.log('✅ Success condition met, showing success toast')
        showToast('Transfer note created successfully', 'success')
        setShowCreateModal(false)
        resetForm()
        fetchTransferNotes()
      } else {
        console.log('❌ Success condition NOT met, showing error toast')
        showToast(response.message || 'Failed to create transfer note', 'error')
      }
    } catch (error) {
      console.error('🔥 CATCH BLOCK - Error creating transfer note:', error)
      console.error('🔥 Error type:', typeof error)
      console.error('🔥 Error message:', error.message)
      console.error('🔥 Error response:', error.response)
      console.error('🔥 Full error object:', JSON.stringify(error, null, 2))
      
      // Check if this is a validation error that should be shown to user
      if (error.message && error.message.includes('Insufficient stock')) {
        showToast(error.message, 'error')
      } else {
        showToast(error.message || 'Error creating transfer note', 'error')
      }
    } finally {
      setFormLoading(false)
    }
  }

  // Handle edit transfer
  const handleEditTransfer = async () => {
    try {
      setFormLoading(true)
      
      // Process transferred items to ensure proper PO item ID format (same as create)
      const processedTransferredItems = formData.transferredItems.map((item, index) => {
        console.log(`📦 Processing update item ${index}:`, item)
        
        // Extract proper PO item ID (handle MongoDB ObjectId format)
        let poItemId = item.poItemId
        if (typeof poItemId === 'object' && poItemId.$oid) {
          poItemId = poItemId.$oid
        }
        
        console.log(`🆔 Update PO Item ID for item ${index}: {raw: '${item.poItemId}', extracted: '${poItemId}'}`)
        
        return {
          poItemId: poItemId,
          transferredQuantity: Number(item.transferredQuantity),
          unitPrice: Number(item.unitPrice || 0)
        }
      })
      
      const updateData = {
        transferredBy: formData.transferredBy,
        transferredDate: formData.transferredDate,
        transferredItems: processedTransferredItems,
        note: formData.note
      }
      
      console.log('📦 Update data being sent:', JSON.stringify(updateData, null, 2))

      const response = await warehouseTransferService.updateTransferNote(selectedTransfer._id, updateData)
      
      console.log('🔥 Update successful - transferredBy field updated:', response.warehouseTransferNote?.transferredBy)
      
      // Check for success in multiple ways (same as create function)
      const isSuccess = response.success === true || 
                       response.success === undefined && response.warehouseTransferNote ||
                       response.statusCode === 200 ||
                       response.data?.warehouseTransferNote
      
      if (isSuccess) {
        showToast('Transfer note updated successfully', 'success')
        setShowEditModal(false)
        resetForm()
        fetchTransferNotes()
      } else {
        showToast(response.message || 'Failed to update transfer note', 'error')
      }
    } catch (error) {
      console.error('Error updating transfer note:', error)
      showToast(error.message || 'Error updating transfer note', 'error')
    } finally {
      setFormLoading(false)
    }
  }

  // Handle delete transfer
  const handleDeleteTransfer = async () => {
    try {
      setFormLoading(true)
      
      const response = await warehouseTransferService.deleteTransferNote(selectedTransfer._id)
      
      if (response.success) {
        showToast('Transfer note deleted successfully', 'success')
        setShowDeleteModal(false)
        setSelectedTransfer(null)
        fetchTransferNotes()
      } else {
        showToast(response.message || 'Failed to delete transfer note', 'error')
      }
    } catch (error) {
      console.error('Error deleting transfer note:', error)
      showToast(error.message || 'Error deleting transfer note', 'error')
    } finally {
      setFormLoading(false)
    }
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      purchaseOrder: '',
      warehouse: '',
      transferredBy: '',
      transferredDate: format(new Date(), 'yyyy-MM-dd'),
      transferredItems: [],
      note: ''
    })
    setSelectedTransfer(null)
  }

  // Open create modal
  const openCreateModal = () => {
    resetForm()
    setShowCreateModal(true)
  }

  // Open edit modal
  const openEditModal = async (transfer) => {
    try {
      setSelectedTransfer(transfer)
      console.log('🔄 Opening edit modal for transfer:', transfer)
      
      // If we have the full PO data with items, use it directly
      if (transfer.purchaseOrder?.orderItems?.length > 0) {
        console.log('✅ Using embedded PO data for edit modal')
        const transferredItems = transfer.transferredItems?.map((item, index) => {
          // Find the corresponding PO item by matching rawMaterialId or index
          const poItem = transfer.purchaseOrder.orderItems.find(poItem => 
            poItem.rawMaterialId === item.rawMaterialId
          ) || transfer.purchaseOrder.orderItems[index]
          
          const poItemId = poItem?._id?.$oid || poItem?._id || item.poItemId
          console.log(`📦 Edit item ${index}: rawMaterialId=${item.rawMaterialId}, poItemId=${poItemId}`)
          
          return {
            poItemId: poItemId,
            transferredQuantity: item.transferredQuantity || 0,
            unitPrice: item.unitPrice || 0
          }
        }) || []
        
        // Format the transferred date properly
        let formattedDate = format(new Date(), 'yyyy-MM-dd') // default to today
        if (transfer.transferredDate) {
          try {
            // Handle different date formats
            const dateStr = typeof transfer.transferredDate === 'string' 
              ? transfer.transferredDate 
              : transfer.transferredDate.toString()
            
            console.log('🗓️ Original transferredDate:', transfer.transferredDate)
            console.log('🗓️ Date string to parse:', dateStr)
            
            // If it's already in yyyy-MM-dd format, use it directly
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
              formattedDate = dateStr
            } else {
              // Parse and format the date
              const parsedDate = new Date(dateStr)
              if (!isNaN(parsedDate.getTime())) {
                formattedDate = format(parsedDate, 'yyyy-MM-dd')
              }
            }
            console.log('🗓️ Final formatted date:', formattedDate)
          } catch (error) {
            console.warn('⚠️ Date parsing error:', error)
            formattedDate = format(new Date(), 'yyyy-MM-dd')
          }
        }
        
        console.log('👤 Original transferredBy value:', transfer.transferredBy)
        console.log('👤 Type of transferredBy:', typeof transfer.transferredBy)
        console.log('👤 transferredBy truthy check:', !!transfer.transferredBy)
        
        // Fix: If transferredBy is a date string, it means backend has wrong data, use empty string
        let cleanTransferredBy = transfer.transferredBy || ''
        if (typeof cleanTransferredBy === 'string' && cleanTransferredBy.includes('GMT')) {
          console.log('⚠️ transferredBy contains date format, clearing it')
          cleanTransferredBy = ''
        }
        
        const formDataToSet = {
          purchaseOrder: transfer.purchaseOrder._id,
          warehouse: transfer.warehouse || '',
          transferredBy: cleanTransferredBy,
          transferredDate: formattedDate,
          transferredItems: transferredItems,
          note: transfer.note || ''
        }
        
        console.log('👤 Setting form data:', formDataToSet)
        console.log('👤 transferredBy in form data (cleaned):', formDataToSet.transferredBy)
        
        setFormData(formDataToSet)
      } else {
        console.log('⚠️ No embedded PO data, fetching PO details...')
        // Fetch the full PO data if not embedded
        const poId = transfer.purchaseOrder?._id || transfer.purchaseOrder
        if (poId) {
          const poResponse = await purchaseOrderService.getPurchaseOrderById(poId)
          if (poResponse.success && poResponse.data) {
            const po = poResponse.data
            console.log('📦 Fetched PO for edit:', po)
            
            const transferredItems = transfer.transferredItems?.map((item, index) => {
              const poItem = po.orderItems.find(poItem => 
                poItem.rawMaterialId === item.rawMaterialId
              ) || po.orderItems[index]
              
              const poItemId = poItem?._id?.$oid || poItem?._id
              console.log(`📦 Edit item ${index}: rawMaterialId=${item.rawMaterialId}, poItemId=${poItemId}`)
              
              return {
                poItemId: poItemId,
                transferredQuantity: item.transferredQuantity || 0,
                unitPrice: item.unitPrice || 0
              }
            }) || []
            
            // Format the transferred date properly (same logic as above)
            let formattedDate = format(new Date(), 'yyyy-MM-dd') // default to today
            if (transfer.transferredDate) {
              try {
                const dateStr = typeof transfer.transferredDate === 'string' 
                  ? transfer.transferredDate 
                  : transfer.transferredDate.toString()
                
                console.log('🗓️ Original transferredDate (fetched PO):', transfer.transferredDate)
                console.log('🗓️ Date string to parse (fetched PO):', dateStr)
                
                if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                  formattedDate = dateStr
                } else {
                  const parsedDate = new Date(dateStr)
                  if (!isNaN(parsedDate.getTime())) {
                    formattedDate = format(parsedDate, 'yyyy-MM-dd')
                  }
                }
                console.log('🗓️ Final formatted date (fetched PO):', formattedDate)
              } catch (error) {
                console.warn('⚠️ Date parsing error (fetched PO):', error)
                formattedDate = format(new Date(), 'yyyy-MM-dd')
              }
            }
            
            console.log('👤 Original transferredBy value (fetched PO):', transfer.transferredBy)
            console.log('👤 Type of transferredBy (fetched PO):', typeof transfer.transferredBy)
            
            // Fix: If transferredBy is a date string, it means backend has wrong data, use empty string
            let cleanTransferredBy = transfer.transferredBy || ''
            if (typeof cleanTransferredBy === 'string' && cleanTransferredBy.includes('GMT')) {
              console.log('⚠️ transferredBy contains date format (fetched PO), clearing it')
              cleanTransferredBy = ''
            }
            
            const formDataToSet = {
              purchaseOrder: poId,
              warehouse: transfer.warehouse || '',
              transferredBy: cleanTransferredBy,
              transferredDate: formattedDate,
              transferredItems: transferredItems,
              note: transfer.note || ''
            }
            
            console.log('👤 Setting form data (fetched PO):', formDataToSet)
            console.log('👤 transferredBy in form data (fetched PO, cleaned):', formDataToSet.transferredBy)
            
            setFormData(formDataToSet)
          }
        }
      }
      
      setShowEditModal(true)
    } catch (error) {
      console.error('❌ Error opening edit modal:', error)
      showToast('Error loading transfer data for editing', 'error')
    }
  }

  // Open view modal
  const openViewModal = (transfer) => {
    setSelectedTransfer(transfer)
    setShowViewModal(true)
  }

  // Open delete modal
  const openDeleteModal = (transfer) => {
    setSelectedTransfer(transfer)
    setShowDeleteModal(true)
  }


  // Handle PO selection for create
  const handlePOSelection = async (poId) => {
    const selectedPO = purchaseOrders.find(po => po._id === poId)
    console.log('🔄 Selected PO Full Object:', JSON.stringify(selectedPO, null, 2))
    
    if (selectedPO) {
      // Extract warehouse from PO (it exists in the PO structure)
      const warehouseId = selectedPO.warehouse?.$oid || selectedPO.warehouse?._id || selectedPO.warehouse || ''
      console.log('🏢 Extracted warehouse from PO:', warehouseId)

      // Fetch warehouse stock for this warehouse to validate available quantities
      const rmStockMap = new Map()
      const prodStockMap = new Map()
      if (warehouseId) {
        try {
          const [rmRes, prodRes] = await Promise.all([
            rawMaterialInventoryService.getInventoriesByBranch(warehouseId, { limit: 1000 }).catch(() => null),
            productInventoryService.getAllInventories({ branch: warehouseId, limit: 1000 }).catch(() => null)
          ])

          const rmList = rmRes?.data?.rawMaterialInventories || rmRes?.rawMaterialInventories || rmRes?.data?.results || rmRes?.data || []
          if (Array.isArray(rmList)) {
            rmList.forEach(inv => {
              const rId = inv.rawMaterial?._id || inv.rawMaterial
              if (rId) rmStockMap.set(String(rId), safeNumber(inv.currentStock, 0))
            })
          }

          const prodList = prodRes?.data?.productInventories || prodRes?.productInventories || prodRes?.data?.results || prodRes?.data || []
          if (Array.isArray(prodList)) {
            prodList.forEach(inv => {
              const pId = inv.product?._id || inv.product
              if (pId) prodStockMap.set(String(pId), safeNumber(inv.currentStock, 0))
            })
          }
        } catch (stockErr) {
          console.warn('⚠️ Could not load warehouse stock for transfer items:', stockErr)
        }
      }
      
      // Process items with async raw material fetching
      const transferredItemsPromises = selectedPO.orderItems?.map(async (item, index) => {
        console.log(`📋 Processing PO item ${index} (FULL OBJECT):`, JSON.stringify(item, null, 2))
        
        if (!item._id) {
          console.error('❌ CRITICAL: PO item missing _id:', item)
          return null // This will cause issues
        }
        
        // Try to get unit price from multiple sources
        let defaultUnitPrice = 0
        
        // First priority: Check if the PO item itself has a unitPrice
        if (item.unitPrice && item.unitPrice > 0) {
          defaultUnitPrice = safeNumber(item.unitPrice, 0)
          console.log('✅ Using PO item unitPrice:', defaultUnitPrice)
        }
        // For raw materials, fetch from API to get current unit price
        else if (item.itemType === 'raw_material' && item.rawMaterialId) {
          try {
            console.log('📦 Fetching raw material details for ID:', item.rawMaterialId)
            const response = await RawMaterialsService.getRawMaterialById(item.rawMaterialId)
            
            console.log('📦 Raw material API response:', response)
            console.log('📦 Response data:', response?.data)
            
            if (response?.success && response.data) {
              const rawMaterial = response.data.rawMaterial || response.data
              console.log('📦 Raw material object:', JSON.stringify(rawMaterial, null, 2))
              console.log('📦 Raw material cost:', rawMaterial.cost)
              console.log('📦 Raw material price:', rawMaterial.price)
              console.log('📦 Raw material unitPrice:', rawMaterial.unitPrice)
              console.log('📦 Raw material purchasePrice:', rawMaterial.purchasePrice)
              
              // Try different possible field names
              const possiblePrice = rawMaterial.cost || rawMaterial.price || rawMaterial.unitPrice || rawMaterial.purchasePrice
              
              if (possiblePrice && possiblePrice > 0) {
                defaultUnitPrice = safeNumber(possiblePrice, 0)
                console.log('✅ Using raw material price from API:', defaultUnitPrice)
              } else {
                console.warn('⚠️ Raw material has no cost/price in API response:', rawMaterial)
              }
            } else {
              console.warn('⚠️ Invalid API response structure:', response)
            }
          } catch (apiError) {
            console.error('❌ Error fetching raw material from API:', apiError)
            // Fallback to snapshot cost if API fails
            if (item.rawMaterialSnapshot?.cost) {
              defaultUnitPrice = safeNumber(item.rawMaterialSnapshot.cost, 0)
              console.log('📦 Fallback: Using rawMaterialSnapshot cost:', defaultUnitPrice)
            }
          }
        }
        // For products or if raw material fetch failed, use existing logic
        else if (item.rawMaterialSnapshot?.cost) {
          defaultUnitPrice = safeNumber(item.rawMaterialSnapshot.cost, 0)
          console.log('📦 Using rawMaterialSnapshot cost:', defaultUnitPrice)
        }
        else if (item.productSnapshot?.cost) {
          defaultUnitPrice = safeNumber(item.productSnapshot.cost, 0)
          console.log('📦 Using productSnapshot cost:', defaultUnitPrice)
        }
        // Last fallback to price field
        else if (item.price) {
          defaultUnitPrice = safeNumber(item.price, 0)
          console.log('📦 Using item price:', defaultUnitPrice)
        }
        
        console.log('💰 Final default unit price:', defaultUnitPrice)
        
        // Extract PO item ID - handle MongoDB ObjectId format
        const poItemId = item._id?.$oid || item._id || ''
        console.log(`🆔 PO Item ID for item ${index}:`, {
          'raw_id': item._id,
          'extracted': poItemId
        })
        
        // Extract item name from productSnapshot OR rawMaterialSnapshot
        const itemName = item.productSnapshot?.name || item.rawMaterialSnapshot?.name || 'Unknown Item'
        const itemUnit = item.productSnapshot?.unit?.abbreviation || item.rawMaterialSnapshot?.unit?.abbreviation || ''
        
        // Extract IDs
        const rawMatId = item.rawMaterialId?.$oid || item.rawMaterialId?._id || item.rawMaterialId || ''
        const prodId = item.productId?.$oid || item.productId?._id || item.productId || ''

        // Check available stock in warehouse
        let availableStock = null
        if (item.itemType === 'raw_material' && rawMatId) {
          if (rmStockMap.has(String(rawMatId))) {
            availableStock = rmStockMap.get(String(rawMatId))
          }
        } else if (prodId) {
          if (prodStockMap.has(String(prodId))) {
            availableStock = prodStockMap.get(String(prodId))
          }
        }

        const orderedQuantity = safeNumber(item.quantity, 0)

        // Smart default for transferredQuantity:
        // If availableStock is known and less than ordered, default to availableStock (or 0 if negative)
        // to prevent auto-requesting more stock than available in warehouse
        let initialTransferredQty = orderedQuantity
        if (availableStock !== null && availableStock < orderedQuantity) {
          initialTransferredQty = Math.max(0, availableStock)
        }

        // Use the actual PO item _id (handle ObjectId format)
        const transferItem = {
          poItemId: poItemId, // Use extracted PO item _id 
          orderedQuantity: orderedQuantity,
          availableStock: availableStock,
          transferredQuantity: initialTransferredQty,
          unitPrice: defaultUnitPrice,
          itemName: itemName, // Add item name
          itemUnit: itemUnit, // Add unit abbreviation
          itemType: item.itemType,
          productId: prodId,
          rawMaterialId: rawMatId
        }
        
        console.log(`📦 Generated transfer item ${index}:`, transferItem)
        return transferItem
      }) || []
      
      // Wait for all promises to resolve
      const transferredItems = (await Promise.all(transferredItemsPromises)).filter(Boolean) || [] // Remove null items
      
      setFormData(prev => {
        const newFormData = {
          ...prev,
          purchaseOrder: poId,
          warehouse: warehouseId, // Set warehouse from PO
          transferredItems: transferredItems
        }
        
        console.log('📋 Updated form data:', newFormData)
        console.log('📋 Transferred items structure:', transferredItems)
        return newFormData
      })
    } else {
      console.log('❌ No PO found with ID:', poId)
    }
  }

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A'
    return format(new Date(date), 'dd/MM/yyyy')
  }

  // Format currency
  const formatCurrency = (amount) => {
    return `Rs. ${new Intl.NumberFormat('en-IN').format(amount || 0)}`
  }

  // Get warehouse name
  const getWarehouseName = (warehouseId) => {
    const warehouse = warehouses.find(w => w._id === warehouseId)
    return warehouse?.name || 'N/A'
  }

  // Get branch name
  const getBranchName = (branchId) => {
    const branch = branches.find(b => b._id === branchId)
    return branch?.name || 'N/A'
  }

  // Get PO display with requesting branch
  const getPODisplay = (purchaseOrder) => {
    if (!purchaseOrder) return 'N/A'
    const poNumber = purchaseOrder.poNumber || 'N/A'
    
    // Handle both populated object and ID reference
    let branchName = 'Unknown'
    if (purchaseOrder.requestingBranch) {
      if (typeof purchaseOrder.requestingBranch === 'object' && purchaseOrder.requestingBranch.name) {
        // Requesting branch is already populated with full object
        branchName = purchaseOrder.requestingBranch.name
      } else {
        // Requesting branch is just an ID, look it up in branches array
        branchName = getBranchName(purchaseOrder.requestingBranch)
      }
    }
    
    return `${poNumber} - ${branchName}`
  }

  // Get statistics
  const getStatistics = () => {
    const total = transferNotes.length
    const today = new Date()
    const thisMonth = transferNotes.filter(transfer => {
      const transferDate = new Date(transfer.transferredDate)
      return transferDate.getMonth() === today.getMonth() && 
             transferDate.getFullYear() === today.getFullYear()
    }).length
    const totalAmount = transferNotes.reduce((sum, transfer) => sum + (transfer.totalAmount || 0), 0)
    const avgAmount = total > 0 ? totalAmount / total : 0

    return { total, thisMonth, totalAmount, avgAmount }
  }

  const statistics = getStatistics()

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Products Transfer</h1>
            
          </div>
          <div className="flex space-x-3">
            <Button 
              onClick={fetchTransferNotes}
              variant="outline"
              disabled={loading}
              className="bg-white border-gray-300"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              onClick={openCreateModal}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Transfer
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Total Transfers */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <ArrowRightLeft className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Transfers</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* This Month */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Calendar className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">This Month</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics.thisMonth}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Amount */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <DollarSign className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Value</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.totalAmount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Average Transfer */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <TrendingUp className="h-8 w-8 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Avg. Transfer</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.avgAmount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Demo Notice */}
        {warehouses.length > 0 && warehouses[0]._id === '1' && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-blue-800 font-medium">Demo Mode</h3>
              <p className="text-blue-700 text-sm mt-1">
                The warehouse management API is not yet implemented. This page shows the UI design and functionality 
                with sample data. All create, edit, and delete operations are simulated.
              </p>
            </div>
          </div>
        </div>
      )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            {/* Filter Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-5 w-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Filter Transfer Notes</h2>
              </div>
              {(searchTerm || branchFilter) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm('')
                    setBranchFilter('')
                  }}
                  className="text-orange-500 border-orange-500 hover:bg-orange-50"
                >
                  Clear All
                </Button>
              )}
            </div>

            {/* Filter Controls */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Search Input - Shorter */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
                <Input
                  placeholder="Search by WTN number, PO number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 h-10 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                />
              </div>

              {/* Branch Filter - Available to all users */}
              <div className="relative">
                <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
                <select
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white text-sm"
                >
                  <option value="">All Branches</option>
                  {branches.map(branch => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
                {/* Dropdown Arrow */}
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
            
            {/* Results Count */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Showing {filteredTransferNotes.length} of {transferNotes.length} transfer notes
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Transfer Notes List */}
        <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <ArrowRightLeft className="h-5 w-5 text-orange-600 mr-2" />
            <span>Transfer Notes Directory</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <InlineLoader />
            </div>
          ) : filteredTransferNotes.length > 0 ? (
            <div className="overflow-x-auto">
              {/* Desktop Table */}
              <div className="hidden lg:block">
                <table className="w-full min-w-full bg-white">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        WTN Number
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        PO Number
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Warehouse
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Branch
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Amount
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transferred By
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredTransferNotes.map((transfer) => (
                      <tr key={transfer._id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-900">
                              {formatDate(transfer.transferredDate)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-orange-100 rounded-lg">
                              <ArrowRightLeft className="h-4 w-4 text-orange-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{transfer.wtnNumber}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-gray-900">
                            {getPODisplay(transfer.purchaseOrder)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Truck className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-900">
                              {getWarehouseName(transfer.warehouse)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-gray-900">
                            {getBranchName(transfer.requestingBranch)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">

                            <span className="font-medium text-gray-900">
                              {formatCurrency(transfer.totalAmount)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-900">
                              {cleanTransferredBy(transfer.transferredBy)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center space-x-1">
                            <button
                              onClick={() => openViewModal(transfer)}
                              className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                              title="View Transfer"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openEditModal(transfer)}
                              className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                              title="Edit Transfer"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openDeleteModal(transfer)}
                              className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                              title="Delete Transfer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-4 p-4">
                {filteredTransferNotes.map((transfer) => (
                  <div
                    key={transfer._id}
                    className="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-orange-100 rounded-lg">
                          <ArrowRightLeft className="h-4 w-4 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{transfer.wtnNumber}</p>
                          <p className="text-sm text-gray-500">PO: {getPODisplay(transfer.purchaseOrder)}</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-gray-500">Date</p>
                        <p className="text-gray-900">{formatDate(transfer.transferredDate)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Total Amount</p>
                        <p className="text-gray-900 font-medium">{formatCurrency(transfer.totalAmount)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Warehouse</p>
                        <p className="text-gray-900 truncate">{getWarehouseName(transfer.warehouse)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Branch</p>
                        <p className="text-gray-900">{getBranchName(transfer.requestingBranch)}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          Transferred by {cleanTransferredBy(transfer.transferredBy)}
                        </span>
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openViewModal(transfer)}
                          className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                          title="View Transfer"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(transfer)}
                          className="text-blue-400 hover:text-blue-600 p-1 rounded transition-colors"
                          title="Edit Transfer"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(transfer)}
                          className="text-red-400 hover:text-red-600 p-1 rounded transition-colors"
                          title="Delete Transfer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <ArrowRightLeft className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No transfer notes found</p>
              <Button
                onClick={openCreateModal}
                className="mt-4 bg-orange-600 hover:bg-orange-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Transfer
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-xl">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Create Transfer Note</h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>
              <div className="px-6 pb-6">
                <CreateTransferForm
                  formData={formData}
                  setFormData={setFormData}
                  purchaseOrders={purchaseOrders}
                  warehouses={warehouses}
                  branches={branches}
                  onPOSelection={handlePOSelection}
                  onSubmit={handleCreateTransfer}
                  loading={formLoading}
                  onCancel={() => setShowCreateModal(false)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-xl">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Edit Transfer Note</h3>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>
              <div className="px-6 pb-6">
                <EditTransferForm
                  formData={formData}
                  setFormData={setFormData}
                  selectedTransfer={selectedTransfer}
                  onSubmit={handleEditTransfer}
                  loading={formLoading}
                  onCancel={() => setShowEditModal(false)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-xl">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Transfer Note Details</h3>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>
              <div className="px-6 pb-6">
                {selectedTransfer && (
                  <ViewTransferDetails
                    transfer={selectedTransfer}
                    warehouses={warehouses}
                    branches={branches}
                    formatDate={formatDate}
                    formatCurrency={formatCurrency}
                    getWarehouseName={getWarehouseName}
                    getBranchName={getBranchName}
                    getPODisplay={getPODisplay}
                    onClose={() => setShowViewModal(false)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Confirm Delete</h3>
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>
              <div className="px-6 py-4">
                {selectedTransfer && (
                  <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h3 className="text-red-800 font-medium">Delete Transfer Note</h3>
                          <p className="text-red-700 text-sm mt-1">
                            Are you sure you want to delete transfer note {selectedTransfer.wtnNumber}? 
                            This action cannot be undone.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3">
                      <Button
                        variant="outline"
                        onClick={() => setShowDeleteModal(false)}
                        disabled={formLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleDeleteTransfer}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        disabled={formLoading}
                      >
                        {formLoading ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Toast */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </div>
  )
}

// Create Transfer Form Component
const CreateTransferForm = ({ 
  formData, 
  setFormData, 
  purchaseOrders,
  warehouses,
  branches,
  onPOSelection,
  onSubmit, 
  loading, 
  onCancel 
}) => {
  // Helper to get branch name from ID or object
  const getBranchName = (branchData) => {
    // If it's already an object with name, return it
    if (branchData && typeof branchData === 'object' && branchData.name) {
      return branchData.name
    }
    // Otherwise treat it as an ID and look it up
    const branch = branches.find(b => b._id === branchData)
    return branch?.name || 'Unknown'
  }
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.transferredItems]
    updatedItems[index] = { ...updatedItems[index], [field]: value }
    setFormData(prev => ({ ...prev, transferredItems: updatedItems }))
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Purchase Order *
          </label>
          <select
            value={formData.purchaseOrder}
            onChange={(e) => onPOSelection(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            required
          >
            <option value="">Select Purchase Order</option>
            {purchaseOrders && purchaseOrders.length > 0 ? (
              purchaseOrders.map(po => (
                <option key={po._id} value={po._id}>
                  {po.poNumber} - {getBranchName(po.requestingBranch)}
                </option>
              ))
            ) : (
              <option disabled>No Purchase Orders Available</option>
            )}
          </select>
          {purchaseOrders && purchaseOrders.length === 0 && (
            <p className="text-sm text-gray-500 mt-1">
              💡 Tip: Create purchase orders first to enable transfers
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Warehouse
          </label>
          <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
            {formData.warehouse ? (
              <span className="text-gray-700">
                {warehouses.find(w => w._id === formData.warehouse)?.name || 'Warehouse from Purchase Order'}
              </span>
            ) : (
              <span className="text-gray-500">Select a Purchase Order to see warehouse</span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Warehouse is automatically selected from the Purchase Order
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Transferred By *
          </label>
          <Input
            name="transferredBy"
            value={formData.transferredBy}
            onChange={handleInputChange}
            placeholder="Enter transferred by"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Transfer Date *
          </label>
          <Input
            type="date"
            name="transferredDate"
            value={formData.transferredDate}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>

      {formData.transferredItems.length > 0 && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Transfer Items ({formData.transferredItems.length})</h3>
            <span className="text-xs text-gray-500">Check stock availability before transfer</span>
          </div>
          <div className="space-y-3">
            {formData.transferredItems.map((item, index) => {
              const isOverOrdered = item.orderedQuantity !== undefined && safeNumber(item.transferredQuantity, 0) > safeNumber(item.orderedQuantity, 0)
              const isOverStock = item.availableStock !== null && item.availableStock !== undefined && safeNumber(item.transferredQuantity, 0) > safeNumber(item.availableStock, 0)
              const hasError = isOverOrdered || isOverStock

              return (
                <div 
                  key={index} 
                  className={`border rounded-lg p-4 transition-colors ${
                    hasError ? 'border-red-300 bg-red-50/20' : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="mb-3 pb-3 border-b border-gray-100 flex flex-wrap justify-between items-center gap-2">
                    <h4 className="text-sm font-semibold text-gray-900">
                      {item.itemName || `Item ${index + 1}`}
                      {item.itemUnit && <span className="text-gray-500 font-normal ml-1">({item.itemUnit})</span>}
                    </h4>
                    <div className="flex items-center gap-2 text-xs">
                      {item.orderedQuantity !== undefined && (
                        <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-200 font-medium">
                          Ordered: {item.orderedQuantity} {item.itemUnit || ''}
                        </span>
                      )}
                      {item.availableStock !== null && item.availableStock !== undefined ? (
                        <span className={`px-2 py-0.5 rounded border font-medium ${
                          item.availableStock <= 0
                            ? 'bg-red-50 text-red-700 border-red-200'
                            : item.availableStock < item.orderedQuantity
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-green-50 text-green-700 border-green-200'
                        }`}>
                          In WH: {item.availableStock} {item.itemUnit || ''}
                        </span>
                      ) : (
                        <span className="bg-gray-50 text-gray-600 px-2 py-0.5 rounded border border-gray-200">
                          WH Stock: Checking...
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Transferred Quantity
                        </label>
                        {item.availableStock !== null && item.availableStock !== undefined && (
                          <button
                            type="button"
                            onClick={() => handleItemChange(index, 'transferredQuantity', Math.min(safeNumber(item.orderedQuantity, 999999), Math.max(0, item.availableStock)))}
                            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium hover:underline"
                          >
                            Set Max ({Math.min(safeNumber(item.orderedQuantity, 999999), Math.max(0, item.availableStock))})
                          </button>
                        )}
                      </div>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.transferredQuantity !== undefined ? item.transferredQuantity : ''}
                        onChange={(e) => handleItemChange(index, 'transferredQuantity', e.target.value)}
                        placeholder="0"
                        className={hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50' : ''}
                      />
                      {isOverOrdered && (
                        <p className="text-xs text-red-600 mt-1 font-medium flex items-center gap-1">
                          <span>⚠️</span> Cannot exceed PO ordered quantity ({item.orderedQuantity})
                        </p>
                      )}
                      {isOverStock && !isOverOrdered && (
                        <p className="text-xs text-red-600 mt-1 font-medium flex items-center gap-1">
                          <span>⚠️</span> Cannot exceed warehouse stock ({item.availableStock})
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Unit Price
                      </label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unitPrice || ''}
                        onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Total Price
                      </label>
                      <Input
                        value={((safeNumber(item.transferredQuantity, 0)) * (safeNumber(item.unitPrice, 0))).toFixed(2)}
                        readOnly
                        className="bg-gray-50"
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Notes
        </label>
        <textarea
          name="note"
          value={formData.note}
          onChange={handleInputChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
          placeholder="Enter any notes..."
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t">
        <Button variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={loading || !formData.purchaseOrder || !formData.transferredBy}
          className="bg-orange-600 hover:bg-orange-700"
        >
          {loading ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4 mr-2" />
              Create Transfer
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

// Edit Transfer Form Component
const EditTransferForm = ({ 
  formData, 
  setFormData, 
  selectedTransfer,
  onSubmit, 
  loading, 
  onCancel 
}) => {
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.transferredItems]
    updatedItems[index] = { ...updatedItems[index], [field]: value }
    setFormData(prev => ({ ...prev, transferredItems: updatedItems }))
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            WTN Number
          </label>
          <Input
            value={selectedTransfer?.wtnNumber || ''}
            readOnly
            className="bg-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            PO Number
          </label>
          <Input
            value={selectedTransfer?.purchaseOrder?.poNumber || ''}
            readOnly
            className="bg-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Transferred By *
          </label>
          <Input
            name="transferredBy"
            value={formData.transferredBy}
            onChange={handleInputChange}
            placeholder="Enter transferred by"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Transfer Date *
          </label>
          <Input
            type="date"
            name="transferredDate"
            value={formData.transferredDate}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>

      {formData.transferredItems.length > 0 && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Transfer Items ({formData.transferredItems.length})</h3>
            <span className="text-xs text-gray-500">Edit transfer quantities</span>
          </div>
          <div className="space-y-3">
            {formData.transferredItems.map((item, index) => {
              const isOverOrdered = item.orderedQuantity !== undefined && safeNumber(item.transferredQuantity, 0) > safeNumber(item.orderedQuantity, 0)
              const isOverStock = item.availableStock !== null && item.availableStock !== undefined && safeNumber(item.transferredQuantity, 0) > safeNumber(item.availableStock, 0)
              const hasError = isOverOrdered || isOverStock

              return (
                <div 
                  key={index} 
                  className={`border rounded-lg p-4 transition-colors ${
                    hasError ? 'border-red-300 bg-red-50/20' : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="mb-3 pb-3 border-b border-gray-100 flex flex-wrap justify-between items-center gap-2">
                    <h4 className="text-sm font-semibold text-gray-900">
                      {item.itemName || `Item ${index + 1}`}
                      {item.itemUnit && <span className="text-gray-500 font-normal ml-1">({item.itemUnit})</span>}
                    </h4>
                    <div className="flex items-center gap-2 text-xs">
                      {item.orderedQuantity !== undefined && (
                        <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-200 font-medium">
                          Ordered: {item.orderedQuantity} {item.itemUnit || ''}
                        </span>
                      )}
                      {item.availableStock !== null && item.availableStock !== undefined ? (
                        <span className={`px-2 py-0.5 rounded border font-medium ${
                          item.availableStock <= 0
                            ? 'bg-red-50 text-red-700 border-red-200'
                            : item.availableStock < item.orderedQuantity
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-green-50 text-green-700 border-green-200'
                        }`}>
                          In WH: {item.availableStock} {item.itemUnit || ''}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Transferred Quantity
                        </label>
                        {item.availableStock !== null && item.availableStock !== undefined && (
                          <button
                            type="button"
                            onClick={() => handleItemChange(index, 'transferredQuantity', Math.min(safeNumber(item.orderedQuantity, 999999), Math.max(0, item.availableStock)))}
                            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium hover:underline"
                          >
                            Set Max ({Math.min(safeNumber(item.orderedQuantity, 999999), Math.max(0, item.availableStock))})
                          </button>
                        )}
                      </div>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.transferredQuantity !== undefined ? item.transferredQuantity : ''}
                        onChange={(e) => handleItemChange(index, 'transferredQuantity', e.target.value)}
                        placeholder="0"
                        className={hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50' : ''}
                      />
                      {isOverOrdered && (
                        <p className="text-xs text-red-600 mt-1 font-medium flex items-center gap-1">
                          <span>⚠️</span> Cannot exceed PO ordered quantity ({item.orderedQuantity})
                        </p>
                      )}
                      {isOverStock && !isOverOrdered && (
                        <p className="text-xs text-red-600 mt-1 font-medium flex items-center gap-1">
                          <span>⚠️</span> Cannot exceed warehouse stock ({item.availableStock})
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Unit Price
                      </label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unitPrice || ''}
                        onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Total Price
                      </label>
                      <Input
                        value={((safeNumber(item.transferredQuantity, 0)) * (safeNumber(item.unitPrice, 0))).toFixed(2)}
                        readOnly
                        className="bg-gray-50"
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Notes
        </label>
        <textarea
          name="note"
          value={formData.note}
          onChange={handleInputChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
          placeholder="Enter any notes..."
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t">
        <Button variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={loading || !formData.transferredBy}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {loading ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Updating...
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4 mr-2" />
              Update Transfer
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

// View Transfer Details Component
const ViewTransferDetails = ({ 
  transfer, 
  formatDate, 
  formatCurrency,
  getWarehouseName,
  getBranchName,
  getPODisplay,
  onClose 
}) => {
  return (
    <div className="space-y-6">
      {/* Transfer Information */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-blue-600" />
            Transfer Information
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">WTN Number</span>
              <span className="font-medium text-gray-900">{transfer.wtnNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">PO Number</span>
              <span className="font-medium text-gray-900">{getPODisplay(transfer.purchaseOrder)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Transfer Date</span>
              <span className="font-medium text-gray-900">{formatDate(transfer.transferredDate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Transferred By</span>
              <span className="font-medium text-gray-900">{cleanTransferredBy(transfer.transferredBy)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Amount</span>
              <span className="font-medium text-green-600">{formatCurrency(transfer.totalAmount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Created By</span>
              <span className="font-medium text-gray-900">{transfer.createdBy?.username || 'N/A'}</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <Building className="h-5 w-5 mr-2 text-green-600" />
            Location Information
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Warehouse</span>
              <span className="font-medium text-gray-900">{getWarehouseName(transfer.warehouse)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Requesting Branch</span>
              <span className="font-medium text-gray-900">{getBranchName(transfer.requestingBranch)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      {transfer.note && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-purple-600" />
            Notes
          </h3>
          <p className="text-sm text-gray-700 whitespace-pre-wrap bg-white p-3 rounded border">
            {transfer.note}
          </p>
        </div>
      )}

      {/* Transferred Items */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
          <Package className="h-5 w-5 mr-2 text-orange-600" />
          Transferred Items ({transfer.transferredItems?.length || 0})
        </h3>
        
        {transfer.transferredItems && transfer.transferredItems.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Item</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Type</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Ordered Qty</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Transferred Qty</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Unit Price</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transfer.transferredItems.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <div>
                        <p className="font-medium text-gray-900">
                          {item.productSnapshot?.name || item.rawMaterialSnapshot?.name || 'Unknown'}
                        </p>
                        <p className="text-gray-500 text-xs">
                          {item.productSnapshot?.sku || item.rawMaterialSnapshot?.code || 'N/A'}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        item.itemType === 'product' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {item.itemType === 'product' ? 'Product' : 'Raw Material'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right font-mono">
                      {item.orderedQuantity || 0} {item.productSnapshot?.unit?.abbreviation || item.rawMaterialSnapshot?.unit?.abbreviation || 'pcs'}
                    </td>
                    <td className="px-4 py-2 text-right font-mono">
                      {item.transferredQuantity || 0} {item.productSnapshot?.unit?.abbreviation || item.rawMaterialSnapshot?.unit?.abbreviation || 'pcs'}
                    </td>
                    <td className="px-4 py-2 text-right font-mono">
                      {formatCurrency(item.unitPrice)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono font-medium">
                      {formatCurrency(item.totalPrice)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="5" className="px-4 py-2 text-right font-medium">
                    Total Amount:
                  </td>
                  <td className="px-4 py-2 text-right font-mono font-bold text-lg">
                    {formatCurrency(transfer.totalAmount)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No items found</p>
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={onClose} variant="outline">
          <X className="h-4 w-4 mr-2" />
          Close
        </Button>
      </div>
    </div>
  )
}

export default ProductsTransfer